import axios from '~/utils/axiosInstance';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { storage } from '~/utils';
import { LANDING_TOASTS, LANDING_TOAST_QUERY_PARAM } from '~/hooks/useLandingToasts/constants';

interface LoginParams {
  email?: string;
  password?: string;
}

interface ResetPasswordParams {
  password: string;
  passwordResetToken: string;
}

export type TwoFactorMethod = 'email' | 'passkey' | 'backup';

export interface LoginResult {
  // Present when the user must complete a second factor.
  requiresTwoFactor?: boolean;
  methods?: TwoFactorMethod[];
  // Present when an admin must enrol in 2FA before continuing.
  requiresTwoFactorSetup?: boolean;
  email?: string;
  // Otherwise this is the logged-in user object.
  [key: string]: any;
}

export const login = async function ({email, password}: LoginParams = {}): Promise<LoginResult> {
  const response = await axios.post('/auth/login', {email, password});
  // Only persist a real user — not a 2FA challenge/setup response.
  if (!response.data?.requiresTwoFactor && !response.data?.requiresTwoFactorSetup) {
    storage.storeCurrentUser(response.data);
  }
  return response.data;
};

export const logout = async function () {
  const response = await axios.post('/auth/logout');
  return response.data;
};

export const verifyPasswordResetToken = async function (token: string) {
  await axios.get(`/auth/password-reset/${token}`);
};

export const sendPasswordResetEmail = async function (email: string) {
  const response = await axios.post('/auth/forgot', {email});
  return response.data;
};

export const resetPassword = async function ({password, passwordResetToken}: ResetPasswordParams) {
  const response = await axios.post('/auth/reset-password', {password, passwordResetToken});
  return response.data;
};

export const confirmEmail = async function (token: string) {
  const response = await axios.post('/auth/confirm-email', { token });
  storage.storeCurrentUser(response.data);
  window.location.href = `/?${LANDING_TOAST_QUERY_PARAM}=${LANDING_TOASTS.email_confirm}`;
  return response.data;
};

export const resendConfirmationEmail = async function () {
  const response = await axios.post('/auth/confirm-email/resend');
  return response.data;
};

// ── Two-factor: verification (challenge-token authorized) ───────────────────

export const sendTwoFactorEmailCode = async function () {
  const response = await axios.post('/auth/2fa/email/send');
  return response.data;
};

export const verifyTwoFactorEmailCode = async function (code: string, rememberDevice = false) {
  const response = await axios.post('/auth/2fa/email/verify', { code, rememberDevice });
  storage.storeCurrentUser(response.data);
  return response.data;
};

export const verifyTwoFactorPasskey = async function (rememberDevice = false) {
  const { data: options } = await axios.post('/auth/2fa/passkey/auth/options');
  const assertion = await startAuthentication(options);
  const response = await axios.post('/auth/2fa/passkey/auth/verify', {
    response: assertion,
    rememberDevice,
  });
  storage.storeCurrentUser(response.data);
  return response.data;
};

export const verifyTwoFactorBackupCode = async function (code: string, rememberDevice = false) {
  const response = await axios.post('/auth/2fa/backup/verify', { code, rememberDevice });
  storage.storeCurrentUser(response.data);
  return response.data;
};

// ── Two-factor: enrollment / management (session or challenge authorized) ───

export const getTwoFactorStatus = async function () {
  const response = await axios.get('/auth/2fa/status');
  return response.data;
};

export const verifyEnrollmentEmailCode = async function (code: string) {
  const response = await axios.post('/auth/2fa/email/verify-enrollment', { code });
  return response.data;
};

export const registerPasskey = async function (deviceName?: string) {
  const { data: options } = await axios.post('/auth/2fa/passkey/register/options');
  const attestation = await startRegistration(options);
  const response = await axios.post('/auth/2fa/passkey/register/verify', {
    response: attestation,
    deviceName,
  });
  return response.data;
};

export const deletePasskey = async function (id: string) {
  const response = await axios.delete(`/auth/2fa/passkey/${id}`);
  return response.data;
};

export const generateBackupCodes = async function (): Promise<{ codes: string[] }> {
  const response = await axios.post('/auth/2fa/backup/generate');
  return response.data;
};

export const finishTwoFactorSetup = async function (rememberDevice = false) {
  const response = await axios.post('/auth/2fa/finish', { rememberDevice });
  // During forced setup the server returns the logged-in user + session cookies.
  if (response.data && !response.data.success) {
    storage.storeCurrentUser(response.data);
  }
  return response.data;
};